import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ApiInternal } from "src/api/api_internal";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import Form from "src/components/base/form";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { Switch, SwitchField } from "src/components/base/switch";
import { Divider } from "src/components/base/divider";

/**
 * The properties for {@link DialogRenewLocalUserAccess}
 */
export type DialogRenewLocalUserAccessProps = {
    /** Uuid of the local user whose access to extend */
    user_uuid: string;
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the request has been submitted */
    onSubmit: () => void;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create an invitation
 */
export default function DialogRenewLocalUserAccess(props: DialogRenewLocalUserAccessProps) {
    const [t] = useTranslation("dialog-renew-local-user-access");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            start: "",
            until: "",
            unlimited: false,
            today: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!value.unlimited && value.until === "") {
                    return {
                        fields: {
                            until: t("error.until-required"),
                        },
                    };
                }

                if (!value.today && value.start === "") {
                    return {
                        fields: {
                            start: t("error.start-required"),
                        },
                    };
                }

                const start = value.today ? null : new Date(value.start);
                const until = value.unlimited ? null : new Date(value.until);

                if (start && start < new Date()) {
                    return {
                        fields: {
                            start: t("error.start-in-past"),
                        },
                    };
                }

                if (until && until < new Date()) {
                    return {
                        fields: {
                            until: t("error.until-in-past"),
                        },
                    };
                }

                if (start && until && start > until) {
                    return {
                        fields: {
                            start: t("error.start-after-until"),
                        },
                    };
                }

                await ApiInternal.users.renewAccess(props.user_uuid, {
                    new_access_start: start?.toISOString(),
                    new_access_until: until?.toISOString(),
                });

                props.onSubmit();
            },
        },
    });
    const unlimited = useStore(form.store, (store) => store.values.unlimited);
    const today = useStore(form.store, (store) => store.values.today);

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.user_uuid]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.renew-access")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field
                                name={"today"}
                                listeners={{
                                    onChange: ({ value, fieldApi }) => {
                                        if (value) {
                                            fieldApi.form.setFieldValue("start", "");
                                        }
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <SwitchField className={"mb-4"}>
                                        <Label>{t("label.today")}</Label>
                                        <Description>{t("description.today")}</Description>
                                        <Switch
                                            color={"blue"}
                                            checked={fieldApi.state.value}
                                            onChange={fieldApi.handleChange}
                                        />
                                    </SwitchField>
                                )}
                            </form.Field>
                        </FieldGroup>

                        <FieldGroup>
                            <form.Field name={"start"}>
                                {(fieldApi) => (
                                    <Field disabled={today}>
                                        <Label>{t("label.start")}</Label>
                                        <Description>{t("description.start")}</Description>
                                        <Input
                                            type={"datetime-local"}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <Divider />

                            <form.Field
                                name={"unlimited"}
                                listeners={{
                                    onChange: ({ value, fieldApi }) => {
                                        if (value) {
                                            fieldApi.form.setFieldValue("start", "");
                                            fieldApi.form.setFieldValue("until", "");
                                        }
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <SwitchField className={"mb-4"}>
                                        <Label>{t("label.unlimited")}</Label>
                                        <Description>{t("description.unlimited")}</Description>
                                        <Switch
                                            color={"blue"}
                                            checked={fieldApi.state.value}
                                            onChange={fieldApi.handleChange}
                                        />
                                    </SwitchField>
                                )}
                            </form.Field>
                            <form.Field name={"until"}>
                                {(fieldApi) => (
                                    <Field disabled={unlimited}>
                                        <Label>{t("label.until")}</Label>
                                        <Description>{t("description.until")}</Description>
                                        <Input
                                            type={"datetime-local"}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.renew-access")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
